import React, { useState, useEffect } from 'react';
import { Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { Form, Input, Select, DatePicker, Checkbox, message, Row, Col, Radio, Divider } from 'antd';
import { userService } from '../../services/userService';
import dayjs from 'dayjs';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { normalizeToCode } from '../../utils/enumNormalizer';

const { TextArea } = Input;

const renderGroupedManagerOptions = (managers = []) => {
    const activeManagers = managers.filter(manager => manager.is_active);
    const deactivatedManagers = managers.filter(manager => !manager.is_active);

    const renderManagerOption = (manager) => (
        <Select.Option
            key={manager.id}
            value={manager.id}
            label={manager.name}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {manager.name}
                    {!manager.is_active && ' (Deactivated)'}
                </span>
                {manager.office_name && (
                    <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0, background: '#f5f5f5', padding: '1px 8px', borderRadius: 3 }}>
                        {manager.office_name}
                    </span>
                )}
            </div>
        </Select.Option>
    );

    return (
        <>
            {activeManagers.length > 0 && (
                <Select.OptGroup label="Active user">
                    {activeManagers.map(renderManagerOption)}
                </Select.OptGroup>
            )}
            {deactivatedManagers.length > 0 && (
                <Select.OptGroup label="Deactivated user">
                    {deactivatedManagers.map(renderManagerOption)}
                </Select.OptGroup>
            )}
        </>
    );
};

const UserFormModal = ({ visible, user, dropdowns, onClose, initialPrejoining }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    // Watch office + freelancer flag so the Statutory section can render the
    // right country block reactively when HR changes the office mid-edit.
    const watchedOfficeId = Form.useWatch('office_id', form);
    const watchedFreelancer = Form.useWatch('freelancer', form);
    const watchedResidencyStatus = Form.useWatch('residency_status', form);
    const watchedCommunityFundOptOut = Form.useWatch('community_fund_opt_out', form);
    const watchedJobStatus = Form.useWatch('job_status', form);
    const selectedOffice = dropdowns.offices?.find(o => o.id === watchedOfficeId);
    const employmentCountry = (selectedOffice?.country || '').toUpperCase() || null;
    const showStatutory = !watchedFreelancer && employmentCountry && ['ID', 'SG', 'PH'].includes(employmentCountry);

    useEffect(() => {
        if (visible) {
            if (user) {
                // Edit mode - populate form
                const pi = user.user_personal_info || {};
                form.setFieldsValue({
                    email: user.email,
                    first_name: user.first_name,
                    middle_name: user.middle_name,
                    last_name: user.last_name,
                    job_title: user.job_title,
                    office_id: user.office?.id,
                    public_holiday_id: user.public_holiday_id,
                    department_id: user.department?.id,
                    manager_id: user.manager?.id,
                    sub_manager_ids: user.sub_managers?.map(sm => sm.id) || [],
                    join_date: user.join_date ? dayjs(user.join_date) : null,
                    job_permanent_date: user.job_permanent_date ? dayjs(user.job_permanent_date) : null,
                    job_expiry_date: user.job_expiry_date ? dayjs(user.job_expiry_date) : null,
                    job_terminate_date: user.job_terminate_date ? dayjs(user.job_terminate_date) : null,
                    job_terminate_reason: normalizeToCode(user.job_terminate_reason, 'job_terminate_reason'),
                    freelancer: user.freelancer || false,
                    is_active: user.is_active !== undefined ? user.is_active : true,
                    job_status: user.job_status || 'Probation',
                    job_description: user.job_description,
                    job_responsibility: user.job_responsibility,
                    role: user.roles?.[0] || 'ROLE_STAFF', // Single role, take first from array
                    all_office_access: user.all_office_access || false,
                    // Statutory fields (May 2026 PayHub mandatory-field brief).
                    tax_status: pi.tax_status || null,
                    tax_residency_status: pi.tax_residency_status || null,
                    residency_status: pi.residency_status || null,
                    pr_start_date: pi.pr_start_date ? dayjs(pi.pr_start_date) : null,
                    community_fund: pi.community_fund || null,
                    community_fund_opt_out: pi.community_fund_opt_out === true,
                    sss_enabled: pi.sss_enabled === null ? null : pi.sss_enabled,
                    philhealth_enabled: pi.philhealth_enabled === null ? null : pi.philhealth_enabled,
                    pagibig_enabled: pi.pagibig_enabled === null ? null : pi.pagibig_enabled,
                });
            } else {
                // Create mode - reset form
                form.resetFields();
                // Set defaults for create mode
                form.setFieldsValue({
                    is_active: true,
                    freelancer: false,
                    all_office_access: false,
                    role: 'ROLE_STAFF', // Default to Staff role
                    ...(initialPrejoining ? {
                        first_name: initialPrejoining.answers.firstName, last_name: initialPrejoining.answers.lastName,
                        gender: initialPrejoining.answers.gender, nationality: initialPrejoining.answers.nationality,
                        residency_status: initialPrejoining.answers.residency, job_title: initialPrejoining.jobTitle,
                        office_id: initialPrejoining.office_id, freelancer: initialPrejoining.employment_type === 'FREELANCER',
                        department_id: initialPrejoining.department_id || undefined,
                        manager_id: initialPrejoining.manager_id || undefined,
                        join_date: initialPrejoining.startDate ? dayjs(initialPrejoining.startDate) : null,
                        job_expiry_date: initialPrejoining.startDate ? dayjs(initialPrejoining.startDate).add(3, 'months') : null,
                    } : {}),
                });
            }
        }
    }, [visible, user, form, initialPrejoining]);

    // Auto-calculate probation end date (join date + 3 months)
    const handleJoinDateChange = (date) => {
        if (date && !user) {
            const probationEnd = dayjs(date).add(3, 'months');
            form.setFieldsValue({ job_expiry_date: probationEnd });
        }
    };

    // Auto-enable all office access for management roles
    const handleRoleChange = (role) => {
        if (role === 'ROLE_MANAGEMENT' || role === 'ROLE_OWNER') {
            form.setFieldsValue({ all_office_access: true });
        }
    };

    // Handle office change to filter leave types
    const handleOfficeChange = (officeId) => {
        // Default public holiday calendar to the selected office
        form.setFieldsValue({ public_holiday_id: officeId });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Format dates for API
            const payload = {
                ...values,
                ...(!user && initialPrejoining ? { prejoining_form_id: initialPrejoining.id,
                    prejoining_revision: initialPrejoining.revision,
                    recruit_application_id: initialPrejoining.application_id || null } : {}),
                roles: [values.role], // Convert single role to array for backend
                join_date: values.join_date ? values.join_date.format('YYYY-MM-DD') : null,
                job_permanent_date: values.job_permanent_date ? values.job_permanent_date.format('YYYY-MM-DD') : null,
                job_expiry_date: values.job_expiry_date ? values.job_expiry_date.format('YYYY-MM-DD') : null,
                job_terminate_date: values.job_terminate_date ? values.job_terminate_date.format('YYYY-MM-DD') : null,
                pr_start_date: values.pr_start_date ? values.pr_start_date.format('YYYY-MM-DD') : null,
            };

            // Remove the single 'role' field after converting to 'roles' array
            delete payload.role;

            // Map public_holiday_id to public_holiday_office_id for create
            if (!user && payload.public_holiday_id) {
                payload.public_holiday_office_id = payload.public_holiday_id;
            }
            delete payload.public_holiday_id;

            // Remove edit-only fields from create mode
            if (!user) {
                delete payload.job_permanent_date;
                delete payload.job_terminate_date;
                delete payload.job_terminate_reason;
                delete payload.job_description;
                delete payload.job_responsibility;
                delete payload.is_active;
            }

            if (user) {
                await userService.updateUser(user.id, payload);
                message.success('User updated successfully');
            } else {
                const result = await userService.createUser(payload);
                if (result.leaveEntitlements) {
                    message.success(`User created successfully with ${result.leaveEntitlements} leave entitlements`);
                } else {
                    message.success('User created successfully');
                }
            }

            onClose(true); // Refresh list
        } catch (error) {
            if (error.errorFields) {
                // Ant Design validation errors
                message.error('Please fill in all required fields');
            } else {
                message.error(getApiErrorMessage(error, `Failed to ${user ? 'update' : 'create'} user`));
            }
        } finally {
            setLoading(false);
        }
    };

    const isEditMode = !!user;

    return (
        <Modal visible={visible} onClose={() => onClose(false)} width="xl">
            <Modal.Header title={user ? 'Edit User' : 'Add User'} />
            <Modal.Body padding="default">
                <Form form={form} layout="vertical">
                    {/* Create from Candidate - Only in Add mode */}
                    {!isEditMode && initialPrejoining && <p>Creating from the reviewed pre-joining form. Personal details and the identity document will be copied into the profile. Enter the employee’s company email below.</p>}
                    {!isEditMode && !initialPrejoining && (
                        <>
                            <Form.Item
                                name="create_from_candidate"
                                valuePropName="checked"
                            >
                                <Checkbox style={{ fontWeight: 600 }}>Create from Candidate</Checkbox>
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.create_from_candidate !== currentValues.create_from_candidate
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('create_from_candidate') ? (
                                        <Form.Item
                                            name="recruit_application_id"
                                            rules={[{ required: true, message: 'Please select a candidate' }]}
                                        >
                                            <Select
                                                placeholder="-- Select One --"
                                                showSearch
                                                optionLabelProp="label"
                                                filterOption={(input, option) => {
                                                    const searchText = `${option.label} ${option.vacancy || ''} ${option.seniority || ''}`.toLowerCase();
                                                    return searchText.includes(input.toLowerCase());
                                                }}
                                                onChange={(value) => {
                                                    const applicant = dropdowns.recruitApplicants?.find(a => a.id === value);
                                                    if (applicant) {
                                                        let firstName = applicant.first_name;
                                                        let lastName = applicant.last_name;
                                                        // Fallback: parse from combined name if separate fields not available
                                                        if (!firstName && !lastName && applicant.name) {
                                                            const parts = applicant.name.split(' ');
                                                            firstName = parts[0] || '';
                                                            lastName = parts.slice(1).join(' ') || '';
                                                        }
                                                        form.setFieldsValue({
                                                            first_name: firstName,
                                                            last_name: lastName,
                                                            job_title: applicant.vacancy,
                                                        });
                                                    }
                                                }}
                                            >
                                                {dropdowns.recruitApplicants?.map(applicant => (
                                                    <Select.Option
                                                        key={applicant.id}
                                                        value={applicant.id}
                                                        label={`${applicant.id} - ${applicant.name}`}
                                                        vacancy={applicant.vacancy}
                                                        seniority={applicant.seniorityLevel}
                                                    >
                                                        <div style={{ lineHeight: '1.4' }}>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {applicant.id} - {applicant.name}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', textTransform: 'lowercase' }}>
                                                                {applicant.vacancy}
                                                                {applicant.seniorityLevel && ` (${applicant.seniorityLevel})`}
                                                            </div>
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                            <Divider />
                        </>
                    )}

                    <Row gutter={24}>
                        {/* Column 1 - Email, Roles, Name */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter email' },
                                    { type: 'email', message: 'Please enter valid email' },
                                    { max: 128, message: 'Maximum 128 characters' }
                                ]}
                            >
                                <Input
                                    placeholder="user@example.com"
                                    maxLength={128}
                                    disabled={isEditMode}
                                    autoComplete="off"
                                />
                            </Form.Item>

                            <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'flex-end' }}>
                                <Form.Item
                                    label="Role"
                                    name="role"
                                    rules={[{ required: true, message: 'Please select a role' }]}
                                    style={{ flex: 1, marginBottom: 0 }}
                                >
                                    <Select
                                        placeholder="Select role"
                                        onChange={handleRoleChange}
                                    >
                                        {dropdowns.roles?.map(role => (
                                            <Select.Option
                                                key={role.key}
                                                value={role.key}
                                                disabled={role.key === 'ROLE_OWNER' && !dropdowns.roles?.find(r => r.key === 'ROLE_OWNER')}
                                            >
                                                {role.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="all_office_access"
                                    valuePropName="checked"
                                    style={{ marginBottom: 0 }}
                                    tooltip="Grants access to all offices (Management and Owner have this by default)"
                                >
                                    <Checkbox>All Office</Checkbox>
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="First Name"
                                name="first_name"
                                rules={[
                                    { required: true, message: 'Please enter first name' },
                                    { max: 64, message: 'Maximum 64 characters' }
                                ]}
                            >
                                <Input placeholder="First name" maxLength={64} autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                                label="Middle Name"
                                name="middle_name"
                                rules={[{ max: 128, message: 'Maximum 128 characters' }]}
                            >
                                <Input placeholder="Middle name (optional)" maxLength={128} autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                                label="Last Name"
                                name="last_name"
                                rules={[
                                    { required: true, message: 'Please enter last name' },
                                    { max: 128, message: 'Maximum 128 characters' }
                                ]}
                            >
                                <Input placeholder="Last name" maxLength={128} autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                                label="Gender"
                                name="gender"
                                rules={[{ required: true, message: 'Please select gender' }]}
                            >
                                <Select placeholder="Select gender">
                                    <Select.Option value="male">Male</Select.Option>
                                    <Select.Option value="female">Female</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Column 2 - Organization */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Job Title"
                                name="job_title"
                                rules={[
                                    { required: true, message: 'Please enter job title' },
                                    { max: 64, message: 'Maximum 64 characters' }
                                ]}
                            >
                                <Input placeholder="e.g., Software Engineer" maxLength={64} autoComplete="off" disabled={!!initialPrejoining && !user} />
                            </Form.Item>

                            <Form.Item
                                label="Office"
                                name="office_id"
                                rules={[{ required: true, message: 'Please select office' }]}
                            >
                                <Select
                                    placeholder="Select office"
                                    disabled={!!initialPrejoining && !user}
                                    showSearch
                                    onChange={handleOfficeChange}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {dropdowns.offices?.map(office => (
                                        <Select.Option key={office.id} value={office.id}>
                                            {office.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Public Holiday"
                                name="public_holiday_id"
                                help="Select which office's holiday calendar to follow"
                            >
                                <Select
                                    placeholder="Select public holiday calendar"
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {dropdowns.offices?.map(office => (
                                        <Select.Option key={office.id} value={office.id}>
                                            {office.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Is Freelancer"
                                name="freelancer"
                            >
                                <Select disabled={!!initialPrejoining && !user}>
                                    <Select.Option value={false}>No</Select.Option>
                                    <Select.Option value={true}>Yes</Select.Option>
                                </Select>
                            </Form.Item>

                            {!!initialPrejoining && !user && <p>Job title, office, employment type, and any saved start date and department come from the reviewed pre-joining form. To change them, update and resend the form first.</p>}

                            <Form.Item
                                label="Primary Department"
                                name="department_id"
                                rules={[{ required: true, message: 'Please select department' }]}
                            >
                                <Select
                                    disabled={!!initialPrejoining?.department_id && !user}
                                    placeholder="Select department"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {dropdowns.departments?.map(dept => (
                                        <Select.Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Manager"
                                name="manager_id"
                            >
                                <Select
                                    placeholder="Select manager (optional)"
                                    disabled={!!initialPrejoining?.manager_id && !user}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option.label || '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionLabelProp="label"
                                >
                                    {renderGroupedManagerOptions(dropdowns.managers)}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Sub Managers"
                                name="sub_manager_ids"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select sub managers (optional)"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option.label || '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionLabelProp="label"
                                >
                                    {renderGroupedManagerOptions(dropdowns.managers)}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Column 3 - Employment & Leave Types */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Join Date"
                                name="join_date"
                                rules={[{ required: true, message: 'Please select join date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    disabled={!!initialPrejoining?.start_date && !user}
                                    onChange={handleJoinDateChange}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Probation End Date"
                                name="job_expiry_date"
                                help={!isEditMode ? "Auto-calculated as join date + 3 months" : undefined}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    disabled={!isEditMode}
                                />
                            </Form.Item>

                            {/* Edit-only fields */}
                            {isEditMode && (
                                <>
                                    <Form.Item
                                        label="Employment Status"
                                        name="job_status"
                                    >
                                        <Select placeholder="Select employment status">
                                            <Select.Option value="Probation">Probation</Select.Option>
                                            <Select.Option value="Permanent">Permanent</Select.Option>
                                            <Select.Option value="Time-Limited">Time-Limited</Select.Option>
                                            <Select.Option value="Temporary">Temporary</Select.Option>
                                            <Select.Option value="Exiting">Exiting</Select.Option>
                                            <Select.Option value="Ended">Ended</Select.Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        label="Permanent Date"
                                        name="job_permanent_date"
                                        rules={[{ required: normalizeToCode(watchedJobStatus, 'job_status') === 'permanent', message: 'Please select permanent date' }]}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Last Day (Termination)"
                                        name="job_terminate_date"
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Termination Reason"
                                        name="job_terminate_reason"
                                    >
                                        <Select placeholder="Select reason (optional)" allowClear>
                                            <Select.Option value="resign">Resign</Select.Option>
                                            <Select.Option value="end_of_contract">End of Contract</Select.Option>
                                            <Select.Option value="end_of_internship">End of Internship</Select.Option>
                                            <Select.Option value="probation_failed">Probation Failed</Select.Option>
                                            <Select.Option value="terminate">Terminate</Select.Option>
                                            <Select.Option value="prolonged_illness">Prolonged Illness</Select.Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        label="Account Status"
                                        name="is_active"
                                    >
                                        <Radio.Group>
                                            <Radio value={true}>Active</Radio>
                                            <Radio value={false}>Deactivated</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item
                                        label="Job Description"
                                        name="job_description"
                                    >
                                        <TextArea
                                            placeholder="Job description (optional)"
                                            rows={4}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Job Responsibility"
                                        name="job_responsibility"
                                        help="Each new line will be transformed into a list item"
                                    >
                                        <TextArea
                                            placeholder="Job responsibilities (optional)"
                                            rows={4}
                                        />
                                    </Form.Item>
                                </>
                            )}
                        </Col>
                    </Row>

                    {/* Statutory & Tax — country-conditional, FTE-only.
                        Mirrors PayHub's submit-time hard-block (May 2026
                        mandatory-fields brief). Hidden for freelancers and
                        for countries with no enforcement (HK / MY today).  */}
                    {showStatutory && (
                        <>
                            <Divider />
                            <Row gutter={24}>
                                <Col span={24}>
                                    <h4 style={{ marginBottom: 12 }}>
                                        Statutory & Tax — {employmentCountry}
                                    </h4>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                {employmentCountry === 'ID' && (
                                    <>
                                        <Col span={12}>
                                            <Form.Item
                                                label="PTKP (tax_status)"
                                                name="tax_status"
                                                rules={[{ required: true, message: 'PTKP code required for ID employees' }]}
                                                tooltip="Indonesian PTKP filing-status code (drives PPh21)"
                                            >
                                                <Select placeholder="Select PTKP code" allowClear>
                                                    {['TK0','TK1','TK2','TK3','K0','K1','K2','K3'].map(code => (
                                                        <Select.Option key={code} value={code}>{code}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Tax residency"
                                                name="tax_residency_status"
                                            >
                                                <Select placeholder="Resident / Non-resident" allowClear>
                                                    <Select.Option value="RESIDENT">Resident</Select.Option>
                                                    <Select.Option value="NON_RESIDENT">Non-resident</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                                {employmentCountry === 'SG' && (
                                    <>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Residency status"
                                                name="residency_status"
                                                rules={[{ required: true, message: 'Residency status required for SG employees' }]}
                                                tooltip="Drives the CPF rate (CITIZEN / PR / FOREIGNER)"
                                            >
                                                <Select placeholder="Select residency" allowClear>
                                                    <Select.Option value="CITIZEN">Citizen</Select.Option>
                                                    <Select.Option value="PR">Permanent Resident</Select.Option>
                                                    <Select.Option value="FOREIGNER">Foreigner</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        {watchedResidencyStatus === 'PR' && (
                                            <Col span={12}>
                                                <Form.Item
                                                    label="PR start date"
                                                    name="pr_start_date"
                                                    rules={[{ required: true, message: 'PR start date required when residency=PR' }]}
                                                    tooltip="Drives the 2-year graduated CPF schedule"
                                                >
                                                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                                </Form.Item>
                                            </Col>
                                        )}
                                        <Col span={12}>
                                            <Form.Item
                                                label="Community fund"
                                                name="community_fund"
                                                rules={[{
                                                    validator: (_, value) => {
                                                        if (watchedCommunityFundOptOut === true) return Promise.resolve();
                                                        if (!value) return Promise.reject(new Error('Select a fund or tick opt-out'));
                                                        return Promise.resolve();
                                                    },
                                                }]}
                                            >
                                                <Select placeholder="CDAC / SINDA / MBMF / ECF" allowClear disabled={watchedCommunityFundOptOut === true}>
                                                    <Select.Option value="CDAC">CDAC</Select.Option>
                                                    <Select.Option value="SINDA">SINDA</Select.Option>
                                                    <Select.Option value="MBMF">MBMF</Select.Option>
                                                    <Select.Option value="ECF">ECF</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Opt out of community fund"
                                                name="community_fund_opt_out"
                                                valuePropName="checked"
                                                tooltip="Tick if the employee has elected to opt out of CDAC/SINDA/MBMF/ECF"
                                            >
                                                <Checkbox>Opted out</Checkbox>
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                                {employmentCountry === 'PH' && (
                                    <>
                                        <Col span={8}>
                                            <Form.Item
                                                label="SSS enrolment"
                                                name="sss_enabled"
                                                rules={[{
                                                    validator: (_, value) =>
                                                        value === true || value === false
                                                            ? Promise.resolve()
                                                            : Promise.reject(new Error('Set explicitly')),
                                                }]}
                                            >
                                                <Select placeholder="Yes / No" allowClear>
                                                    <Select.Option value={true}>Enrolled</Select.Option>
                                                    <Select.Option value={false}>Not enrolled</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item
                                                label="PhilHealth enrolment"
                                                name="philhealth_enabled"
                                                rules={[{
                                                    validator: (_, value) =>
                                                        value === true || value === false
                                                            ? Promise.resolve()
                                                            : Promise.reject(new Error('Set explicitly')),
                                                }]}
                                            >
                                                <Select placeholder="Yes / No" allowClear>
                                                    <Select.Option value={true}>Enrolled</Select.Option>
                                                    <Select.Option value={false}>Not enrolled</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item
                                                label="Pag-IBIG enrolment"
                                                name="pagibig_enabled"
                                                rules={[{
                                                    validator: (_, value) =>
                                                        value === true || value === false
                                                            ? Promise.resolve()
                                                            : Promise.reject(new Error('Set explicitly')),
                                                }]}
                                            >
                                                <Select placeholder="Yes / No" allowClear>
                                                    <Select.Option value={true}>Enrolled</Select.Option>
                                                    <Select.Option value={false}>Not enrolled</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <button
                                                type="button"
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    color: '#555',
                                                    padding: '2px 10px',
                                                    marginBottom: 8,
                                                }}
                                                onClick={() => form.setFieldsValue({
                                                    sss_enabled: false,
                                                    philhealth_enabled: false,
                                                    pagibig_enabled: false,
                                                })}
                                            >
                                                Not enrolled in any scheme — set all to No
                                            </button>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </>
                    )}

                    {/* Checklists - Only in Add mode */}
                    {!isEditMode && (
                        <>
                            <Divider />
                            <Row gutter={24}>
                                <Col span={24}>
                                    <Form.Item
                                        label="Checklists"
                                        name="checklist_ids"
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select onboarding checklists (optional)"
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {dropdowns.checklists?.map(checklist => (
                                                <Select.Option key={checklist.id} value={checklist.id}>
                                                    {checklist.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer buttons={[
                {
                    ...BUTTON_PRESETS.CANCEL,
                    key: 'cancel',
                    label: 'Cancel',
                    onClick: () => onClose(false)
                },
                {
                    ...BUTTON_PRESETS.SAVE,
                    key: 'save',
                    label: user ? 'Update' : 'Create',
                    loading,
                    onClick: handleSave
                }
            ]} />
        </Modal>
    );
};

export default UserFormModal;
